import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, Rocket, Terminal, WifiOff, Wifi } from 'lucide-react'
import ConnectHostModal from '../../../../components/Modal/ConnectHostModal'
import BootstrapRouterModal from '../../../../components/Modal/BootstrapRouterModal'
import { isVpnHandshakeFresh } from '../../../../utils/vpn'

/**
 * Seção de bootstrap inicial do ManagedDevice.
 * Resolve o fluxo certo por Kind/Category:
 *   - MikrotikRouter     → BootstrapRouterModal (.rsc, MikrotikBootstrapCapabilityHandler)
 *   - Linux* / CPMais    → ConnectHostModal     (shell, LinuxBootstrapCapabilityHandler,
 *                                                cobre Ubuntu/Debian/RedHat/Oracle/Raspberry/Proxmox)
 * Demais kinds (Windows, Android, Switch, AP, Camera, CLP, ...) não têm bootstrap por este caminho.
 */
export default function BootstrapSection({ device }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const flow = useMemo(() => resolveFlow(device), [device])
  if (!flow) return null

  const done = flow === 'mikrotik'
    ? !!device.bootstrapCompletedAt
    : device.provisioningStatus === 'Ready'

  // "Concluído" só diz que o script rodou; a VPN é confirmada pelo handshake do túnel.
  const vpnConnected = device.vpnPeerId ? isVpnHandshakeFresh(device.vpnPeerLastHandshake) : null

  return (
    <section className="border border-gray-200 bg-white rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <Rocket className="w-4 h-4 text-theme-primary" />
        <h3 className="text-sm font-semibold text-gray-800">
          {t('managedDeviceDetail.bootstrapTitle', 'Bootstrap')}
        </h3>
        {done && (
          <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-0.5">
            <CheckCircle2 className="w-3.5 h-3.5" />
            {t('managedDeviceDetail.bootstrapDone', 'Concluído')}
          </span>
        )}
        {done && vpnConnected === true && (
          <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-0.5">
            <Wifi className="w-3.5 h-3.5" />
            {t('managedDeviceDetail.bootstrapVpnConnected', 'VPN conectada')}
          </span>
        )}
        {done && vpnConnected === false && (
          <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
            <WifiOff className="w-3.5 h-3.5" />
            {t('managedDeviceDetail.bootstrapVpnNotConnected', 'VPN não conectada')}
          </span>
        )}
      </div>

      <p className="text-xs text-gray-600 mb-3">
        {flow === 'mikrotik'
          ? t(
              'managedDeviceDetail.bootstrapHintMikrotik',
              'Gera um link temporário e os comandos para colar no terminal do MikroTik (Winbox → New Terminal). O roteador baixa o .rsc, cria o usuário, sobe o túnel WireGuard e confirma automaticamente.',
            )
          : t(
              'managedDeviceDetail.bootstrapHintLinux',
              'Gera o comando curl/wget para rodar como root no host Linux. O script instala WireGuard, cria o usuário, instala a chave SSH e marca o provisionamento como concluído.',
            )}
      </p>

      <button
        type="button"
        onClick={() => setOpen(true)}
        className="btn btn-primary btn-sm inline-flex items-center gap-2"
      >
        <Terminal className="w-4 h-4" />
        {done
          ? t('managedDeviceDetail.bootstrapRegenerate', 'Gerar novo link de bootstrap')
          : t('managedDeviceDetail.bootstrapGenerate', 'Gerar link de bootstrap')}
      </button>

      {flow === 'mikrotik' && (
        <BootstrapRouterModal
          isOpen={open}
          onClose={() => setOpen(false)}
          router={device}
        />
      )}

      {flow === 'linux' && (
        <ConnectHostModal
          isOpen={open}
          onClose={() => setOpen(false)}
          host={{ ...device, hostKind: device.kind }}
        />
      )}
    </section>
  )
}

function resolveFlow(device) {
  if (!device) return null
  if (device.kind === 'MikrotikRouter') return 'mikrotik'
  const kind = device.kind || ''
  const isLinuxKind = kind.startsWith('Linux') || kind === 'CPMais'
  if (device.kindCategory === 'Host' && isLinuxKind) return 'linux'
  return null
}
